/**
 * 67.73's orbit census (the owner's pick): the sightings' markers drawn as one point draw instead of a sprite each — 25
 * draws at 25 a side, one a sighting (layer0Views.ts syncSightings: each a Sprite with its own material, 64 px, the size
 * kept on screen). The same painted markers (trackMarker.ts drawTrackMarker), each look painted once into a cell of one
 * atlas; the points sample their cell, each the size on screen its sprite had (`markerPointPx`), over the scene as the
 * sprites were (no depth test, drawn late). Presentation only.
 */
import * as THREE from 'three';

/** The atlas: cells of `cell` px, `cols` a side (64 looks; a look is a kind × a confidence step × a colour). */
export const MARKER_ATLAS = { cell: 64, cols: 8 } as const;

/** The pixels across a sprite of `scale` with sizeAttenuation off spans (three's sprite: `scale` at a view depth of 1), as a point's size. */
export function markerPointPx(scale: number, bufferHeight: number, projection11: number): number {
  return (scale * bufferHeight * projection11) / 2;
}

/** Cells by look: the cell a key takes (a new key the next free; past the last, the last is reused), and its corner in the atlas (uv, from the bottom left). */
export class MarkerCells {
  private readonly byKey = new Map<string, number>();
  /** The key's cell, and whether it is new (to be painted). */
  cell(key: string): { index: number; fresh: boolean } {
    const hit = this.byKey.get(key);
    if (hit !== undefined) return { index: hit, fresh: false };
    const index = Math.min(this.byKey.size, MARKER_ATLAS.cols * MARKER_ATLAS.cols - 1);
    this.byKey.set(key, index);
    return { index, fresh: true };
  }
  /** Cell `i`'s corner (u0, v0) and its side (du), the canvas's rows top down as the texture flips them. */
  static uv(i: number): { u0: number; v0: number; du: number } {
    const n = MARKER_ATLAS.cols, du = 1 / n, col = i % n, row = Math.floor(i / n);
    return { u0: col * du, v0: 1 - (row + 1) * du, du };
  }
}

const VERT = /* glsl */ `
attribute vec2 aCell;
uniform float uSize;
varying vec2 vCell;
void main() {
  vCell = aCell;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = uSize;
}`;
const FRAG = /* glsl */ `
uniform sampler2D uAtlas; uniform float uDu;
varying vec2 vCell;
void main() {
  vec4 c = texture2D(uAtlas, vCell + vec2(gl_PointCoord.x, 1.0 - gl_PointCoord.y) * uDu);
  if (c.a < 0.01) discard;
  gl_FragColor = c;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/** The markers of a frame: `begin`, an `add` a marker, `end`; one Points, one draw. */
export class MarkerPoints {
  readonly points: THREE.Points;
  private readonly cells = new MarkerCells();
  private readonly canvas: HTMLCanvasElement;
  private readonly atlas: THREE.CanvasTexture;
  private pos = new Float32Array(3 * 32);
  private uv = new Float32Array(2 * 32);
  private n = 0;
  private readonly uniforms: { uSize: { value: number }; uAtlas: { value: THREE.Texture }; uDu: { value: number } };

  /** `scale`: the sprites' (a view-depth-1 size); `renderOrder` theirs. */
  constructor(private readonly scale: number, renderOrder: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = MARKER_ATLAS.cell * MARKER_ATLAS.cols;
    this.atlas = new THREE.CanvasTexture(this.canvas);
    this.atlas.colorSpace = THREE.SRGBColorSpace;
    this.atlas.generateMipmaps = false; this.atlas.minFilter = THREE.LinearFilter;   // (no mip bleeding across cells)
    this.uniforms = { uSize: { value: 32 }, uAtlas: { value: this.atlas }, uDu: { value: 1 / MARKER_ATLAS.cols } };
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    g.setAttribute('aCell', new THREE.BufferAttribute(this.uv, 2));
    g.setDrawRange(0, 0);
    const mat = new THREE.ShaderMaterial({ uniforms: this.uniforms, vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthTest: false, depthWrite: false });
    this.points = new THREE.Points(g, mat);
    this.points.frustumCulled = false; this.points.renderOrder = renderOrder; this.points.name = 'markerPoints';
    // (the sprites' size on screen, from this frame's drawing buffer and lens)
    this.points.onBeforeRender = (renderer, _s, camera) => {
      this.uniforms.uSize.value = markerPointPx(this.scale, renderer.getDrawingBufferSize(_size).y, camera.projectionMatrix.elements[5]);
    };
  }

  begin(): void { this.n = 0; }

  /** A marker at `at` in the look `key`, painted by `paint` into its cell the first time the key is seen. */
  add(at: THREE.Vector3, key: string, paint: (g: CanvasRenderingContext2D, size: number) => void): void {
    const { index, fresh } = this.cells.cell(key);
    if (fresh) {
      const g = this.canvas.getContext('2d')!, s = MARKER_ATLAS.cell, x = (index % MARKER_ATLAS.cols) * s, y = Math.floor(index / MARKER_ATLAS.cols) * s;
      g.save(); g.clearRect(x, y, s, s); g.translate(x, y); g.beginPath(); g.rect(0, 0, s, s); g.clip(); paint(g, s); g.restore();
      this.atlas.needsUpdate = true;
    }
    if (this.n * 3 >= this.pos.length) this.grow();
    this.pos[this.n * 3] = at.x; this.pos[this.n * 3 + 1] = at.y; this.pos[this.n * 3 + 2] = at.z;
    const c = MarkerCells.uv(index);
    this.uv[this.n * 2] = c.u0; this.uv[this.n * 2 + 1] = c.v0;
    this.n++;
  }

  end(): void {
    const g = this.points.geometry;
    (g.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (g.getAttribute('aCell') as THREE.BufferAttribute).needsUpdate = true;
    g.setDrawRange(0, this.n);
    this.points.visible = this.n > 0;
  }

  get count(): number { return this.n; }

  private grow(): void {
    const pos = new Float32Array(this.pos.length * 2), uv = new Float32Array(this.uv.length * 2);
    pos.set(this.pos); uv.set(this.uv); this.pos = pos; this.uv = uv;
    const g = this.points.geometry;
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    g.setAttribute('aCell', new THREE.BufferAttribute(this.uv, 2));
  }

  dispose(): void { this.points.removeFromParent(); this.points.geometry.dispose(); (this.points.material as THREE.Material).dispose(); this.atlas.dispose(); }
}

const _size = new THREE.Vector2();
